import {
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  Input,
  NgModuleRef,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

import { BasePortalHost } from './base-portal-host';
import { ComponentPortal, Portal, TemplatePortal } from './portal';

/**
 * Directive version of a `TemplatePortal`. Because the directive *is* a TemplatePortal,
 * the directive instance itself can be attached to a host, enabling declarative use of portals.
 *
 * Usage:
 * <ng-template portal #greeting>
 *   <p> Hello {{name}} </p>
 * </ng-template>
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[portal]',
  exportAs: 'rtPortal',
})
export class TemplatePortalDirective extends TemplatePortal {
  constructor(templateRef: TemplateRef<any>, viewContainerRef: ViewContainerRef) {
    super(templateRef, viewContainerRef);
  }
}

/**
 * Directive version of a PortalHost. Because the directive *is* a PortalHost, portals can be
 * directly attached to it, enabling declarative use.
 *
 * Usage:
 * <ng-template [portalHost]="greeting"></ng-template>
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[portalHost]',
})
export class PortalHostDirective extends BasePortalHost implements OnDestroy {
  public static ngAcceptInputType_portal: '';
  /** The attached portal. */
  private _portal: Portal<any>;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private viewContainerRef: ViewContainerRef,
  ) {
    super();
  }

  @Input('portalHost')
  public get portal(): Portal<any> {
    return this._portal;
  }

  public set portal(p: Portal<any>) {
    if (p) {
      this._replaceAttachedPortal(p);
    }
  }

  public ngOnDestroy(): void {
    this.dispose();
  }

  /**
   * Attach the given ComponentPortal to this PortalHost using the ComponentFactoryResolver.
   *
   * @param portal Portal to be attached to the portal host.
   */
  public attachComponentPortal<T>(portal: ComponentPortal<T>, index?: number): ComponentRef<T> {
    portal.setAttachedHost(this);

    // If the portal specifies an origin, use that as the logical location of the component
    // in the application tree. Otherwise use the location of this PortalHost.
    const viewContainerRef =
      portal.viewContainerRef != null ? portal.viewContainerRef : this.viewContainerRef;

    const injector = portal.injector || viewContainerRef.parentInjector;

    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(
      portal.component,
    );
    const ngModuleRef = injector.get(NgModuleRef);

    const ref = viewContainerRef.createComponent(
      componentFactory,
      index !== undefined ? index : viewContainerRef.length,
      injector,
      undefined,
      ngModuleRef,
    );

    this.setDisposeFn(() => ref.destroy());

    return ref;
  }

  /**
   * Attach the given TemplatePortal to this PortlHost as an embedded View.
   * @param portal Portal to be attached.
   */
  public attachTemplatePortal(portal: TemplatePortal, index?: number): Map<string, any> {
    portal.setAttachedHost(this);

    this.viewContainerRef.createEmbeddedView(portal.templateRef!, null, index || 0);
    this.setDisposeFn(() => this.viewContainerRef.clear());

    // TODO(jelbourn): return locals from view
    return new Map<string, any>();
  }

  /** Detaches the currently attached Portal (if there is one) and attaches the given Portal. */
  private _replaceAttachedPortal(p: Portal<any>): void {
    if (this.hasAttached()) {
      this.detach();
    }

    if (p) {
      this.attach(p);
      this._portal = p;
    }
  }
}
